import { Dispatch, SetStateAction, useState } from 'react';
import { fetchPostOffersSelect } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import clsx from 'clsx';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { TGetOffersResponse } from 'lendingApi/lapiOverrides.types';
import { useGetIntroducerApplication } from 'lendingApi/lendingApiHooks';
import { useNavigate } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { getDurationInMonths } from 'utils/durationHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { IconWithText } from '../IconWithText/IconWithText';
import { renderDurationMessaging } from '../ProductOffers.helper';
import styles from '../ProductOffers.module.css';
import { ConfirmedOfferLogo } from './ConfirmedOfferLogo';

type TProps = {
  offerId: string;
  maxAmount: number;
  rate?: number;
  duration: TGetOffersResponse['data']['offers'][number]['duration'];
  setSelectOfferError: Dispatch<SetStateAction<boolean>>;
};

export const ConfirmedOffer = ({
  offerId,
  maxAmount,
  rate,
  duration,
  setSelectOfferError,
}: TProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { formatCurrency, formatPercentage } = useNumberFormat();
  const { application } = useGetIntroducerApplication();
  const { trackEvent } = useAmplitude();
  const { checkIntroducerPermission } = useIntroducerContext();
  const isFlexi24 = getDurationInMonths(duration) === 24;

  const formaliseOffer = async () => {
    setIsLoading(true);
    trackEvent(AMPLITUDE_EVENTS.acceptRightNowClicked, {
      'Product type': isFlexi24
        ? 'Flexi24 confirmed offer'
        : 'Flexi confirmed offer',
      'Max amount': maxAmount,
    });

    try {
      await fetchPostOffersSelect({ offerId });

      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetApplication'],
      });

      navigate(
        PATH_NAMES.application({ applicationId: application?.application_uid }),
      );
    } catch {
      return setSelectOfferError(true);
    }

    setIsLoading(false);
  };

  return (
    <section className={styles.productOffer}>
      <div className={clsx(styles.group, styles.image)}>
        <ConfirmedOfferLogo className={styles.productImage} />
      </div>
      <div className={clsx(styles.group, styles.confirmedPoints)}>
        <h2 className={styles.confirmedHeading}>Confirmed offer</h2>
        <ul>
          <li>
            <IconWithText
              icon="calendarOutline"
              text={renderDurationMessaging(duration)}
            />
          </li>
          <li>
            <IconWithText
              icon="pound"
              text={`Loan amount: ${formatCurrency(maxAmount)}`}
            />
          </li>
          {isFlexi24 && (
            <li>
              <IconWithText icon="pound" text="Up to 9% commission fee" />
            </li>
          )}
          {!isFlexi24 && rate && (
            <li>
              <IconWithText
                icon="presentationOutline"
                text={`Monthly interest: ${formatPercentage(rate)}`}
              />
            </li>
          )}
        </ul>
      </div>
      <div className={clsx(styles.group, styles.points)}>
        <ul>
          <li>
            <IconWithText icon="lightningBolt" text="Instant decision" />
          </li>
          <li>
            <IconWithText icon="checkSquare" text="No documents required" />
          </li>
        </ul>
        <Button
          loading={isLoading}
          variant="denim"
          onClick={formaliseOffer}
          disabled={!checkIntroducerPermission('can_interact_with_offers')}
        >
          Accept right now
        </Button>
      </div>
    </section>
  );
};
